import React, { useMemo, useState, useEffect } from "react";
import { connect } from "react-redux";
import { compose } from "redux";
import { useQuery, useApolloClient } from "@apollo/react-hooks";
import clone from "lodash/clone";

import {
  withStyles,
  withBreakpoint,
  withMobile,
  withModal,
  withOverlay,
} from "components/common";
import SearchBar from "components/common/SearchBar";
import Table from "components/common/Table";
import sortDate from "utils/sortDate";

import computeColumns from "./helpers";
import { ACTIVITY_QUERY, USER_NAME_QUERY } from "./resolvers";
import { ACTIVITY_STATUS } from "utils/constants";

const [red] = ["#FF8B89"];

const ALLOWED_ACTIVITY_TYPES = [
  ACTIVITY_STATUS.PENDING,
  ACTIVITY_STATUS.SETTLED,
  ACTIVITY_STATUS.DISPUTED,
  ACTIVITY_STATUS.RETURNED,
];

const YourTransactions = ({
  userId,
  breakpointContext: { isBelow },
  mobileContext: { isMobile },
  modalContext: { openModal },
  overlayContext: { openOverlay },
  didUserMakePayment,
  setUserMakePayment,
}) => {
  const [searchInput, setSearchInput] = useState("");
  const [filteredActivity, setFilteredActivity] = useState(null);
  const { loading, error, data, refetch } = useQuery(ACTIVITY_QUERY, {
    variables: { page: 0 },
    fetchPolicy: "network-only",
  });
  const { loading: loadingUser, error: errorUser, data: dataUser } = useQuery(
    USER_NAME_QUERY
  );

  const columns = useMemo(() => computeColumns(isBelow("md")), [isBelow]);
  const client = useApolloClient();

  useEffect(() => {
    if (didUserMakePayment) {
      refetch();
      setUserMakePayment(false);
    }
  }, [didUserMakePayment]);

  if (loading || loadingUser) {
    console.log("Loading");
    return "Loading...";
  }
  if (error || errorUser) {
    console.log((error || errorUser).message);
    return `Error! ${(error || errorUser).message}`;
  }

  const { activity: initialActivity, dispute_reasons } = data;
  const { user } = dataUser || {};
  const disputeUserData = {
    id: userId,
    firstName: user.first_name,
    lastName: user.last_name,
  };
  const filteredInitialActivity = initialActivity.filter((t) =>
    ALLOWED_ACTIVITY_TYPES.includes(t.status && t.status.toUpperCase())
  );

  const getRowStyle = (row) => {
    return {
      backgroundColor: row.original.returned_at ? red : "",
    };
  };

  return (
    <div>
      <div className="font-title font-bold text-xl mb-2">Recent activity</div>
      <SearchBar
        id="transactions-search"
        placeholder="Search"
        value={searchInput}
        onChange={({ value }) => {
          setSearchInput(value);
          client
            .query({
              query: ACTIVITY_QUERY,
              variables: {
                searchStr: value,
                page: 0,
              },
            })
            .then((res) => {
              const { data: { activity = [] } = {} } = res || {};
              setFilteredActivity(
                activity.filter((a) =>
                  ALLOWED_ACTIVITY_TYPES.includes(a.status)
                )
              );
            });
        }}
      />
      <div className="my-5">
        {filteredActivity || filteredInitialActivity.length ? (
          <Table
            id="transactions-table"
            data={clone(
              sortDate(filteredActivity) || sortDate(filteredInitialActivity)
            )}
            columns={columns}
            getRowStyle={getRowStyle}
            onRowClick={(e, row) => {
              const { original: { type } = {} } = row;
              if (type !== "payment") {
                isMobile
                  ? openOverlay(e, "DisputeOverlay", {
                      row,
                      user: disputeUserData,
                    })
                  : openModal(e, "DisputeModal", {
                      row,
                      user: disputeUserData,
                    });
              }
            }}
          />
        ) : (
          <div>You don't have any activity yet!</div>
        )}
      </div>
    </div>
  );
};

export default compose(
  connect((state) => ({
    userId: state.user.id,
  })),
  withMobile,
  withStyles,
  withBreakpoint,
  withOverlay,
  withModal
)(YourTransactions);
